import { useMemo } from 'react';
import { useStoreLocationSearch, type StoreLocation } from '~/shared/hooks/useStoreLocationSearch';
import type { Store } from '~/lib/data-contract/salesforce';
import type { SalesforceProduct } from '~/lib/salesforce/types';
import { sortStoresByLocation } from './reservationStoreSorting';
import type { LocationSearchPayload } from './types';

type UseReservationStoreSearchInput = {
    allStores?: Store[];
    defaultStores?: Store[];
    product?: SalesforceProduct | null;
    trackLocationSearch?: (payload: LocationSearchPayload) => void;
};

const toLocationSearchPayload = ({ source, query }: StoreLocation): LocationSearchPayload =>
    source === 'geo'
        ? { method: 'geolocation' }
        : // Numeric input is a zip search. No Danish city contains digits.
          { method: 'manual_input', inputType: /^\d+$/.test(query?.trim() ?? '') ? 'zip' : 'city' };

/**
 * Store search for the reservation drawer. See `useStoreLocationSearch` for the search state.
 * @param input.allStores - Raw server store list, used as the pool for client-side geo/search sorting.
 * @param input.defaultStores - Composed resting-view list shown when there is no active location.
 * @param input.product - Used to resolve C&C eligibility for the within-group sort.
 * @returns The `useStoreLocationSearch` state plus `nearbyStores`, the list to render.
 */
export const useReservationStoreSearch = ({
    allStores,
    defaultStores,
    product,
    trackLocationSearch,
}: UseReservationStoreSearchInput) => {
    const { activeLocation, ...search } = useStoreLocationSearch({
        onLocationResolved: (location) => trackLocationSearch?.(toLocationSearchPayload(location)),
    });

    // Grouped distance sort around the active location. Without one we show the default list.
    const nearbyStores = useMemo(() => {
        if (!activeLocation || !allStores) return defaultStores;

        return sortStoresByLocation(allStores, activeLocation, product);
    }, [activeLocation, allStores, defaultStores, product]);

    return { ...search, nearbyStores };
};
